/**
 * i18n runtime. English is the source and default language; other languages load on demand.
 * The list of languages lives in ./locales.ts; strings live next to each feature (features/<name>/i18n/<code>.json).
 * See docs/i18n.md.
 */
import i18next, { type Resource, type ResourceLanguage } from 'i18next';
import { initReactI18next } from 'react-i18next';

import { setFormatLocale } from '@/shared/lib/format';
import { readStorage, writeStorage } from '@/shared/lib/storage';

import { DEFAULT_LOCALE, PSEUDO_LOCALES, SUPPORTED_LOCALES, isKnownLocale, localeDefinition } from './locales';

export { DEFAULT_LOCALE, PSEUDO_LOCALES, SUPPORTED_LOCALES, isRtl, localeDefinition, type LocaleDefinition } from './locales';

/** Loads every namespace for a language, e.g. { deals: {...}, common: {...} }. Provided by app/i18n.ts. */
export type LocaleLoader = (code: string) => Promise<ResourceLanguage>;

let loader: LocaleLoader = () => Promise.resolve({});

const pseudo = {
  type: 'postProcessor' as const,
  name: 'pseudo',
  process(value: string) {
    // Lengthen by ~30% and bracket, so overflow and untranslated (unbracketed) text stand out.
    const padded = value.length > 3 ? `${value} ${'·'.repeat(Math.ceil(value.length * 0.3))}` : value;
    return `⟦${padded}⟧`;
  },
};

export function applyDocumentLocale(code: string) {
  const def = localeDefinition(code);
  const root = document.documentElement;
  root.lang = def.pseudo ? 'en' : def.code;
  root.dir = def.dir;
  setFormatLocale(def.code);
}

/** Pseudo locales reuse the English strings; real languages load their JSON once. */
async function ensureLoaded(code: string) {
  const def = localeDefinition(code);
  if (def.pseudo || code === DEFAULT_LOCALE) return;
  if (i18next.isInitialized && i18next.hasResourceBundle(code, 'common')) return;
  const bundles = await loader(code);
  for (const [ns, strings] of Object.entries(bundles)) i18next.addResourceBundle(code, ns, strings, true, true);
}

/** The saved choice (browser), or English. A server preference, when present, overrides it after sign-in. */
export function storedLocale(): string {
  const stored = readStorage<string | null>('locale', null);
  return isKnownLocale(stored) && (SUPPORTED_LOCALES.some((l) => l.code === stored) || PSEUDO_LOCALES.some((l) => l.code === stored)) ? stored : DEFAULT_LOCALE;
}

export async function initI18n(resources: Resource, load: LocaleLoader, defaultNS = 'common') {
  loader = load;
  const lng = storedLocale();
  await i18next
    .use(pseudo)
    .use(initReactI18next)
    .init({
      resources,
      lng: DEFAULT_LOCALE,
      fallbackLng: DEFAULT_LOCALE,
      defaultNS,
      ns: Object.keys(resources[DEFAULT_LOCALE] ?? {}),
      interpolation: { escapeValue: false },
      returnNull: false,
      react: { useSuspense: false },
    });
  await changeLocale(lng, { persist: false });
  return i18next;
}

/** Switches language and direction in place (no reload, no sign-out). */
export async function changeLocale(code: string, { persist = true }: { persist?: boolean } = {}) {
  const def = localeDefinition(code);
  await ensureLoaded(def.code);
  if (persist) writeStorage('locale', def.code);
  i18next.options.postProcess = def.pseudo ? ['pseudo'] : [];
  // Direction and formats first, so languageChanged listeners already see the new <html dir>.
  applyDocumentLocale(def.code);
  await i18next.changeLanguage(def.code);
}

export const currentLocale = () => i18next.resolvedLanguage ?? i18next.language;

export { i18next };
